import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const ProgressTracker = ({ progressData }) => {
  const weeklyProgressData = [
    { week: 'Week 1', workouts: 3, avgWeight: 135 },
    { week: 'Week 2', workouts: 4, avgWeight: 140 },
    { week: 'Week 3', workouts: 3, avgWeight: 145 },
    { week: 'Week 4', workouts: 5, avgWeight: 150 },
    { week: 'Week 5', workouts: 4, avgWeight: 155 },
    { week: 'Week 6', workouts: 4, avgWeight: 160 },
  ];

  const strengthProgressData = [
    { exercise: 'Bench Press', current: 185, previous: 165, pr: 200 },
    { exercise: 'Squat', current: 225, previous: 205, pr: 245 },
    { exercise: 'Deadlift', current: 275, previous: 255, pr: 295 },
    { exercise: 'Overhead Press', current: 115, previous: 105, pr: 125 },
  ];

  const stats = [
    {
      label: 'Total Workouts',
      value: '23',
      change: '+5 this week',
      icon: 'Dumbbell',
      color: 'primary',
    },
    {
      label: 'Personal Records',
      value: '8',
      change: '+2 this month',
      icon: 'Award',
      color: 'success',
    },
    {
      label: 'Avg Duration',
      value: '52 min',
      change: '+3 min',
      icon: 'Clock',
      color: 'accent',
    },
    {
      label: 'Completion Rate',
      value: '87%',
      change: '+12%',
      icon: 'TrendingUp',
      color: 'success',
    },
  ];

  const getColorClass = (color) => {
    const colors = {
      primary: 'bg-primary/10 text-primary',
      success: 'bg-success/10 text-success',
      accent: 'bg-accent/10 text-accent',
    };
    return colors?.[color] || colors?.primary;
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats?.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg ${getColorClass(stat?.color)} flex items-center justify-center`}>
                <Icon name={stat?.icon} size={24} />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{stat?.label}</p>
              <p className="text-3xl font-heading font-bold text-foreground">{stat?.value}</p>
              <p className="text-xs text-success flex items-center gap-1">
                <Icon name="TrendingUp" size={12} />
                {stat?.change}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="card">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Weekly Workout Activity
        </h3>
        <div className="w-full h-64 md:h-80" aria-label="Weekly workout activity bar chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeklyProgressData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="week" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="workouts" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Strength Progression
        </h3>
        <div className="space-y-4">
          {strengthProgressData?.map((exercise, index) => {
            const progress = ((exercise?.current - exercise?.previous) / exercise?.previous) * 100;
            const prProgress = (exercise?.current / exercise?.pr) * 100;
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{exercise?.exercise}</span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">
                      Previous: {exercise?.previous} lbs
                    </span>
                    <span className="font-semibold text-foreground">
                      Current: {exercise?.current} lbs
                    </span>
                    <span className="text-success flex items-center gap-1">
                      <Icon name="TrendingUp" size={14} />
                      +{progress?.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="relative w-full h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="absolute h-full bg-primary/30 transition-all duration-500"
                    style={{ width: `${prProgress}%` }}
                  />
                  <div 
                    className="absolute h-full bg-primary transition-all duration-500"
                    style={{ width: `${(exercise?.current / exercise?.pr) * 100}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>0 lbs</span>
                  <span>PR: {exercise?.pr} lbs</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="card">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Average Weight Progression
        </h3>
        <div className="w-full h-64 md:h-80" aria-label="Average weight progression line chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={weeklyProgressData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="week" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
              />
              <Line 
                type="monotone" 
                dataKey="avgWeight" 
                stroke="var(--color-success)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-success)', r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Recent Personal Records
        </h3>
        <div className="space-y-3">
          {[
            {
              exercise: 'Deadlift',
              weight: '295 lbs',
              date: '2026-01-23',
              improvement: '+10 lbs',
              icon: 'Award',
            },
            {
              exercise: 'Bench Press',
              weight: '200 lbs',
              date: '2026-01-20',
              improvement: '+5 lbs',
              icon: 'Award',
            },
            {
              exercise: 'Squat',
              weight: '245 lbs',
              date: '2026-01-18',
              improvement: '+15 lbs',
              icon: 'Award',
            },
          ]?.map((pr, index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-4 rounded-lg bg-success/5 border border-success/20"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                  <Icon name={pr?.icon} size={20} color="var(--color-success)" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{pr?.exercise}</h4>
                  <p className="text-sm text-muted-foreground">
                    {new Date(pr.date)?.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-foreground">{pr?.weight}</p>
                <p className="text-sm text-success">{pr?.improvement}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressTracker;